// ─── Progressive Lead Store ───────────────────────────────────────────────────
// Captures calculator interactions silently the moment we have a valid email.
// Today: writes to localStorage + console (so nothing is lost while we wait for
//        the Supabase connection).
// Tomorrow: drop a Supabase client into `_supabase` and the same calls upsert
//           into `calculator_leads` automatically — no other code changes.

import { isValidEmail } from './recovery-calculator';

// ── Placeholder for the future Supabase client ───────────────────────────────
// When you wire Supabase, do:
//   import { createClient } from '@supabase/supabase-js';
//   setSupabaseClient(createClient(URL, ANON_KEY));
let _supabase = null;
export function setSupabaseClient(client) { _supabase = client; }
export function hasSupabase() { return !!_supabase; }

const LS_KEY = 'crownledger:calculator_leads';

function readAll() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function writeAll(map) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch { /* quota / SSR */ }
}

/**
 * Upsert a lead row keyed by email.
 * @param {object} payload — partial lead fields. Must contain a valid `email`.
 * @param {'partial'|'submitted'} status
 * @returns {Promise<{ ok: boolean, source: 'supabase'|'local'|'noop', error?: any }>}
 */
export async function upsertLead(payload, status = 'partial') {
  if (!payload || !isValidEmail(payload.email)) {
    return { ok: false, source: 'noop', error: 'invalid email' };
  }

  const now = new Date().toISOString();
  const row = { ...payload, status, updated_at: now };

  // 1. localStorage — always succeeds offline-first
  const map = readAll();
  const existing = map[payload.email] || { created_at: now };
  map[payload.email] = { ...existing, ...row };
  writeAll(map);

  // 2. Supabase if wired
  if (_supabase) {
    try {
      // We use a SECURITY DEFINER RPC instead of a direct table upsert.
      // This bypasses RLS pitfalls and keeps the table locked to the public API.
      const { error } = await _supabase.rpc('upsert_calculator_lead', {
        p_payload: row,
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('[leadStore] supabase rpc error', error);
        return { ok: false, source: 'supabase', error };
      }
      // eslint-disable-next-line no-console
      console.info('[leadStore] supabase rpc ok', payload.email, status);
      return { ok: true, source: 'supabase' };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[leadStore] supabase threw', err);
      return { ok: false, source: 'supabase', error: err };
    }
  }

  // 3. No Supabase yet — local-only mode (visible for QA via window.__leads)
  if (typeof window !== 'undefined') {
    window.__leads = map;
    // eslint-disable-next-line no-console
    console.info('[leadStore] local upsert', payload.email, status, row);
  }
  return { ok: true, source: 'local' };
}

/** Tiny debounce — no lodash needed. */
export function debounce(fn, ms = 600) {
  let t;
  const debounced = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
  debounced.cancel = () => clearTimeout(t);
  return debounced;
}
